import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import './PeopleForm.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  role_or_title: string;
  status: string;
  source: string;
  newsletter_consent: boolean;
  website_or_profile_links: string;
  general_notes: string;
}

interface FormErrors {
  full_name?: string;
}

interface CompanySuggestion {
  id: string;
  company_name: string;
}

const PERSON_STATUSES = [
  'Subscriber',
  'Prospect',
  'Attempted to contact',
  'Connected',
  'Qualified',
  'Opportunity',
  'Customer',
  'Unqualified',
] as const;

// ─── Component ────────────────────────────────────────────────────────────────

const PeopleForm: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    role_or_title: '',
    status: 'Prospect',
    source: '',
    newsletter_consent: false,
    website_or_profile_links: '',
    general_notes: '',
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Company autocomplete state
  const [companySuggestions, setCompanySuggestions] = useState<CompanySuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // ─── Company search ───────────────────────────────────────────────────────────

  const searchCompanies = useCallback(async (query: string) => {
    if (!query.trim() || query.trim().length < 2) {
      setCompanySuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`/api/companies?q=${encodeURIComponent(query)}&page_size=5`);
      if (!res.ok) return;
      const json = await res.json() as { success: boolean; data: CompanySuggestion[] };
      if (json.success) {
        setCompanySuggestions(json.data);
        setShowSuggestions(json.data.length > 0);
      }
    } catch {
      // Silently fail — company search is a convenience, not required
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCompanies(form.company_name);
    }, 300);
    return () => clearTimeout(timer);
  }, [form.company_name, searchCompanies]);

  const selectCompany = (company: CompanySuggestion) => {
    setForm({ ...form, company_name: company.company_name });
    setCompanySuggestions([]);
    setShowSuggestions(false);
  };

  // ─── Validation ──────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.full_name.trim()) {
      newErrors.full_name = 'Full name is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch('/api/people', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          company_name: form.company_name.trim() || null,
          role_or_title: form.role_or_title.trim() || null,
          status: form.status,
          source: form.source.trim() || null,
          newsletter_consent: form.newsletter_consent,
          website_or_profile_links: form.website_or_profile_links.trim() || null,
          general_notes: form.general_notes.trim() || null,
        }),
      });

      const json = await res.json() as {
        success: boolean;
        data?: { id: string };
        message?: string;
      };

      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }

      // Success — navigate to the new person detail page
      navigate(`/people/${json.data!.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create person.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="people-form-page">
      {/* Back navigation */}
      <button className="back-link" onClick={() => navigate('/people')} type="button">
        ← People
      </button>

      {/* Page header */}
      <div className="form-page-header">
        <h1 className="form-page-title">New person</h1>
      </div>

      <Card>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-fields">

            {/* Name */}
            <Input
              label="Full name *"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="e.g. Sarah Chen"
              error={errors.full_name}
              autoFocus
            />

            {/* Contact */}
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="email@example.com"
            />
            <Input
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+44 7700 000000"
            />

            {/* Company with autocomplete */}
            <div className="company-field-wrapper">
              <Input
                label="Company"
                value={form.company_name}
                onChange={(e) => {
                  setForm({ ...form, company_name: e.target.value });
                }}
                onBlur={() => {
                  // Delay hiding so click on suggestion registers
                  setTimeout(() => setShowSuggestions(false), 150);
                }}
                onFocus={() => {
                  if (companySuggestions.length > 0) setShowSuggestions(true);
                }}
                placeholder="Type to search or create a new company"
                autoComplete="off"
              />
              {loadingSuggestions && (
                <div className="company-suggestions-loading">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {showSuggestions && companySuggestions.length > 0 && (
                <ul className="company-suggestions">
                  {companySuggestions.map((company) => (
                    <li key={company.id}>
                      <button
                        type="button"
                        className="company-suggestion-item"
                        onMouseDown={() => selectCompany(company)}
                      >
                        {company.company_name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="company-field-hint">
                Select an existing company or type a new name to create one automatically.
              </p>
            </div>

            {/* Role */}
            <Input
              label="Role / title"
              value={form.role_or_title}
              onChange={(e) => setForm({ ...form, role_or_title: e.target.value })}
              placeholder="e.g. Founder, CTO, Head of Product"
            />

            {/* Status */}
            <div className="input-wrapper">
              <label className="input-label">Status</label>
              <select
                className="input"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {PERSON_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Source */}
            <Input
              label="Source"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              placeholder="e.g. Referral, LinkedIn, Conference, Website enquiry"
            />

            {/* Consent */}
            <div className="input-wrapper">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.newsletter_consent}
                  onChange={(e) => setForm({ ...form, newsletter_consent: e.target.checked })}
                  className="form-checkbox"
                />
                <span>Consent to newsletter / related communications</span>
              </label>
            </div>

            {/* Links */}
            <Input
              label="Website / profile links"
              value={form.website_or_profile_links}
              onChange={(e) => setForm({ ...form, website_or_profile_links: e.target.value })}
              placeholder="https://linkedin.com/in/…"
            />

            {/* Notes */}
            <div className="input-wrapper">
              <label className="input-label">Notes</label>
              <textarea
                className="input form-textarea"
                value={form.general_notes}
                onChange={(e) => setForm({ ...form, general_notes: e.target.value })}
                placeholder="General notes about this person…"
                rows={5}
              />
            </div>

            {saveError && (
              <p className="form-save-error">{saveError}</p>
            )}

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
              >
                {saving ? 'Creating…' : 'Create person'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/people')}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default PeopleForm;
